import * as React from 'react'

import { styled } from '~/styled'
import { overrideStyledVariantValue } from '~/utilities/override-styled-variant-value/overrideStyledVariantValue'

import { Text } from '../text/Text'

export const InputBackground = styled('div', {
  base: [
    'flex',
    'text-grey-1000',
    'transition-[background,border-color]',
    'duration-100',
    'ease-out',
    'w-full'
  ],
  variants: {
    appearance: {
      standard: [
        'bg-white',
        'border',
        'border-grey-800',
        'focus-within:border-blue-800',
        'rounded-md'
      ],
      modern: [
        'bg-grey-100',
        'border-none',
        'focus-within:outline-2',
        'focus-within:outline-blue-800',
        'focus-within:outline-offset-1',
        'focus-within:outline-solid',
        'focus-within:z-1',
        'rounded-md'
      ]
    },
    size: {
      sm: ['h-8'],
      md: ['h-10'],
      lg: ['h-12'],
      xl: ['h-16']
    },
    disabled: {
      true: ['cursor-not-allowed', 'opacity-30']
    },
    state: {
      error: []
    },
    theme: {
      white: [],
      grey: []
    }
  },
  compoundVariants: [
    {
      state: 'error',
      appearance: 'standard',
      class: ['border-danger']
    },
    {
      state: 'error',
      appearance: 'modern',
      class: ['bg-danger-light', 'focus-within:outline-danger']
    },
    {
      appearance: 'modern',
      theme: 'white',
      class: ['bg-white']
    },
    {
      appearance: 'modern',
      theme: 'grey',
      class: ['bg-grey-100']
    }
  ]
})

InputBackground.displayName = 'InputBackground'

const StyledInputText = styled(Text, {
  base: [
    'appearance-none',
    'border-none',
    'bg-none',
    'bg-transparent',
    'shadow-none',
    'box-border',
    'focus:outline-none',
    'px-3',
    'size-full',
    'placeholder:opacity-100',
    'placeholder:text-grey-700'
  ]
})

export type InputTextProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> &
  Omit<React.ComponentProps<typeof StyledInputText>, 'type' | 'as'> & {
    // override default 'type' property to prevent Input from being used to render
    // checkboxes, radios etc — we have dedicated components for them
    type?: 'text' | 'number' | 'email' | 'password' | 'tel' | 'url' | 'search'
    as?: never
    disabled?: boolean
  }

const toTextSize = {
  sm: 'sm',
  md: 'md',
  lg: 'md',
  xl: 'lg'
}

export const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(
  ({ type = 'text', size, ...rest }, ref) => {
    const textSize = React.useMemo(
      () => overrideStyledVariantValue(size, (s) => toTextSize[s]),
      [size]
    )

    return (
      <StyledInputText
        ref={ref}
        as="input"
        type={type === 'number' ? 'text' : type}
        inputMode={type === 'number' ? 'numeric' : undefined}
        pattern={type === 'number' ? '[0-9]*' : undefined}
        size={textSize}
        {...(rest as any)}
      />
    )
  }
)

InputText.displayName = 'InputText'

type InputBackgroundProps = React.ComponentProps<typeof InputBackground>
export type InputProps = Omit<
  React.ComponentProps<typeof InputText>,
  'size' | 'state' | 'appearance'
> & {
  size?: InputBackgroundProps['size']
  state?: InputBackgroundProps['state']
  appearance?: InputBackgroundProps['appearance']
  theme?: InputBackgroundProps['theme']
  disabled?: boolean
  name?: string
  readOnly?: boolean
  value?: string | number
  onChange?: React.ChangeEventHandler<HTMLInputElement>
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      size = 'md',
      appearance = 'standard',
      theme,
      state,
      disabled,
      style,
      ...rest
    },
    ref
  ) => {
    return (
      <InputBackground
        size={size}
        appearance={appearance}
        theme={theme}
        disabled={disabled}
        state={state}
        className={className}
        style={style}
      >
        <InputText size={size} ref={ref} disabled={disabled} {...rest} />
      </InputBackground>
    )
  }
)

Input.displayName = 'Input'
